import React, { useEffect } from "react";
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Tab,
  Tabs,
  Card,
  CardContent,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { fetchData } from "../utils/api";
import { UserContext } from "../App";

export default function QualityCheck() {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();

  // get data from current dataset
  const {
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    currentExperimentProgress,
  } = React.useContext(UserContext);

  const [dataQuality, setDataQuality] = React.useState({
    errors: { "": "" },
    warnings: { "": "" },
  });
  const ref = React.useRef(null);
  const [activeNavIdx, setActiveNavIdx] = React.useState(0);
  const [activeNavItem, setActiveNavItem] = React.useState("errors");
  const [selectedMessage, setSelectedMessage] = React.useState("");
  const [selectedWarningType, setSelectedWarningType] = React.useState("");
  const [showDetails, setShowDetails] = React.useState(false);
  const [selectedColumn, setSelectedColumn] = React.useState("");
  const [numberCategies, setNumberCategies] = React.useState("");
  const [activeColor, setActiveColor] = React.useState("");

  const fetchExperimentDataQuality = () => {
    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx]?.datasetName}/experiment/${experiments[currentExperimentIdx]?.experimentName}/quality`
    )
      .then((data) => {
        // Validate the structure of data
        if (!data || typeof data !== "object") {
          console.error("Invalid API response for data quality:", data);
          setDataQuality({ errors: {}, warnings: {} }); // Fallback structure
          return;
        }

        // Ensure `errors` and `warnings` are properly structured
        const sanitizedData = {
          errors: data.errors || {},
          warnings: data.warnings || {},
        };

        setDataQuality(sanitizedData);
      })
      .catch((error) => {
        console.error("Failed to fetch data quality:", error);
        setDataQuality({ errors: {}, warnings: {} }); // Fallback on failure
      });
  };

  // set Experiment Data by API requests, when the current dataset + experiment is gathered
  useEffect(() => {
    if (datasets?.length > 0 && currentDatasetIdx < datasets.length) {
      fetchExperimentDataQuality();
    }
  }, [datasets, currentDatasetIdx, experiments, currentExperimentIdx]);

  const handleClick = (parentKeyIdx, childKeyIdx) => {
    const parentKey = Object.keys(dataQuality[activeNavItem])[parentKeyIdx];
    const childKey = Object.keys(dataQuality[activeNavItem][parentKey])[
      childKeyIdx
    ];
    const message = dataQuality[activeNavItem][parentKey][childKey];
    const column = message.col_name;
    setSelectedMessage(JSON.stringify(message));
    setSelectedColumn(JSON.stringify(column));
    setSelectedWarningType(parentKey);
    setNumberCategies(JSON.stringify(message.num_categories));

    if (activeNavItem === "errors") {
      setActiveColor("#C34F40");
    }
    if (activeNavItem === "warnings") {
      setActiveColor("#F09642");
    }

    setShowDetails(true);
  };

  const handleChangeTab = (event, newValue) => {
    setActiveNavIdx(newValue);
    setActiveNavItem(newValue === 0 ? "errors" : "warnings");
    setShowDetails(false);
  };

  const countTotalErrors = (errorType) => {
    let totalWarnings = 0;

    // Count the number of errors or warnings
    totalWarnings += Object.values(dataQuality[errorType]).reduce(
      (acc, val) => acc + val.length,
      0
    );

    return totalWarnings;
  };

  return (
    <Grid id="data-quality-container" container>
      <Grid
        container
        sx={{
          padding: theme.spacing(8, 8),
          borderRadius: "16px",
          backgroundColor: "white",
        }}
      >
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            {t("qualityCheck.PageTitle")}
          </Typography>
        </Grid>
        <Tabs
          value={activeNavItem === "errors" ? 0 : 1}
          onChange={handleChangeTab}
          centered
          sx={{
            marginBottom: theme.spacing(2),
            display: "flex",
            justifyContent: "center",
            minWidth: "auto",
            padding: theme.spacing(1, 2),
          }}
        >
          <Tab
            label={
              <Badge
                sx={{ margin: theme.spacing(1, 6) }}
                badgeContent={countTotalErrors("errors")}
                color="error"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    transform: "translateX(-10px)",
                  }}
                >
                  <ErrorIcon />
                  <span>{t("qualityCheck.ErrorsTitle")}</span>
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge
                sx={{ margin: theme.spacing(1, 6) }}
                badgeContent={countTotalErrors("warnings")}
                color="warning"
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    transform: "translateX(-10px)",
                  }}
                >
                  <WarningIcon />
                  <span>{t("qualityCheck.WarningsTitle")}</span>
                </Box>
              </Badge>
            }
          />
        </Tabs>
        {
          // only render if current experiment has fulfilled all required steps and stats are loaded
          // eslint-disable-next-line no-nested-ternary
          currentExperimentProgress?.created && dataQuality.errors ? (
            <Grid item xs={12} sx={{ margin: theme.spacing(2, 0) }}>
              <Box sx={{ p: 5 }} ref={ref}>
                <Grid container sx={{ padding: theme.spacing(0, 0) }}>
                  <Grid
                    item
                    xs={6}
                    sx={{
                      padding: theme.spacing(1, 2),
                      maxHeight: "60vh",
                      overflowY: "scroll",
                    }}
                  >
                    <List>
                      {Object.keys(dataQuality[activeNavItem]).length === 0 ? (
                        <ListItem
                          sx={{
                            backgroundColor: "#f1f1f1",
                            ":hover": { backgroundColor: "#e0e0e0" },
                          }}
                          key={activeNavIdx}
                        >
                          <ListItemText variant="h5">
                            {t("qualityCheck.NoErrors")}
                          </ListItemText>
                        </ListItem>
                      ) : (
                        <>
                          {Object.entries(dataQuality[activeNavItem]).map(
                            ([key, message], outerIdx) =>
                              Array.isArray(message) ? (
                                message.map((msg, idx) => (
                                  <ListItem
                                    key={`${outerIdx}-${idx}`}
                                    onClick={() => handleClick(outerIdx, idx)}
                                    sx={{
                                      backgroundColor:
                                        selectedMessage === JSON.stringify(msg)
                                          ? activeColor
                                          : "#9b9b9b",
                                    }}
                                  >
                                    <Alert
                                      severity="error"
                                      sx={{
                                        backgroundColor: "transparent",
                                        color:
                                          selectedMessage ===
                                          JSON.stringify(message)
                                            ? "inherit"
                                            : "white",
                                      }}
                                    >
                                      <AlertTitle
                                        sx={{
                                          color:
                                            selectedMessage ===
                                            JSON.stringify(message)
                                              ? "inherit"
                                              : "white",
                                        }}
                                      >
                                        {activeNavItem === "errors"
                                          ? t("qualityCheck.ErrorTitle")
                                          : t("qualityCheck.WarningTitle")}
                                        :
                                        {key ===
                                          "colsWithImplausibleValues" && (
                                          <>
                                            {JSON.stringify(msg.col_name)}{" "}
                                            {t(
                                              "qualityCheck.colsWithImplausibleValuesText"
                                            )}
                                          </>
                                        )}
                                        {key ===
                                          "colsWithTooManyCategories" && (
                                          <>
                                            {JSON.stringify(msg.col_name)}{" "}
                                            {t(
                                              "qualityCheck.toManyCategoriesText"
                                            )}
                                          </>
                                        )}
                                        {key === "colsWithTooManyNans" && (
                                          <>
                                            {JSON.stringify(msg.col_name)}{" "}
                                            {t("qualityCheck.toManyNansText")}
                                          </>
                                        )}
                                        {key === "someTargetsConstant" && (
                                          <>
                                            {t(
                                              "qualityCheck.someTargetsConstantText"
                                            )}
                                          </>
                                        )}
                                        {key === "allFeaturesConstant" && (
                                          <>
                                            {t(
                                              "qualityCheck.allFeaturesConstantText"
                                            )}
                                          </>
                                        )}
                                        {key === "allTargetsConstant" && (
                                          <>
                                            {t(
                                              "qualityCheck.allTargetsConstantText"
                                            )}
                                          </>
                                        )}
                                        {key ===
                                          "colsWithIntolerableManyCategories" && (
                                          <>
                                            {JSON.stringify(msg.col_name)}{" "}
                                            {t(
                                              "qualityCheck.colsWithIntolerableManyCategoriesText"
                                            )}
                                          </>
                                        )}
                                      </AlertTitle>
                                    </Alert>
                                  </ListItem>
                                ))
                              ) : (
                                <ListItem key={key}>
                                  <Alert severity="error">
                                    <AlertTitle>
                                      {t("qualityCheck.ErrorTitle")}: {key}
                                    </AlertTitle>
                                    {t("qualityCheck.ErrorDescription")}
                                    {JSON.stringify(message)}
                                  </Alert>
                                </ListItem>
                              )
                          )}
                        </>
                      )}
                    </List>
                  </Grid>
                  <Grid
                    item
                    id="plottingArea"
                    xs={6}
                    sx={{
                      padding: theme.spacing(1, 4),
                      margin: theme.spacing(0, 0),
                    }}
                  >
                    {showDetails && (
                      <Card>
                        <CardContent>
                          <Typography variant="h5">Details</Typography>
                        </CardContent>
                        <CardContent>
                          {selectedWarningType && (
                            <>
                              {selectedWarningType ===
                                "colsWithImplausibleValues" && (
                                <>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {selectedColumn}{" "}
                                    {t(
                                      "qualityCheck.colsWithImplausibleValuesText"
                                    )}
                                  </Typography>
                                  <Typography>
                                    {t(
                                      "qualityCheck.colsWithImplausibleValuesDetails"
                                    )}
                                  </Typography>
                                </>
                              )}
                              {selectedWarningType ===
                                "colsWithTooManyCategories" && (
                                <>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {selectedColumn}{" "}
                                    {t("qualityCheck.toManyCategoriesText")}
                                  </Typography>
                                  <Typography>
                                    {t(
                                      "qualityCheck.colsWithToManyCategoriesNumber"
                                    )}{" "}
                                    {numberCategies}
                                  </Typography>
                                  <Typography>
                                    {t("qualityCheck.toManyCategoriesDetails")}
                                  </Typography>
                                  <Typography variant="h6">
                                    {t("qualityCheck.possibleProblems")}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t("qualityCheck.manyCategoriesProblems")}
                                  </Typography>
                                </>
                              )}
                              {selectedWarningType ===
                                "colsWithTooManyNans" && (
                                <>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {selectedColumn}{" "}
                                    {t("qualityCheck.toManyNansText")} <br />
                                  </Typography>
                                  <Typography>
                                    {t("qualityCheck.toManyNansDetails")}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {" "}
                                    {t("qualityCheck.toManyNansDescription")}
                                  </Typography>
                                </>
                              )}
                              {selectedWarningType ===
                                "someTargetsConstant" && (
                                <>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {t("qualityCheck.constantColValuesText")}
                                  </Typography>
                                  <Typography>
                                    {t("qualityCheck.constantColValuesDetails")}
                                  </Typography>
                                </>
                              )}
                              {selectedWarningType ===
                                "allFeaturesConstant" && (
                                <>
                                  <Typography>
                                    {t("qualityCeck.allFeaturesConstantText")}
                                  </Typography>
                                  <Typography>
                                    {t(
                                      "qualityCeck.allFeaturesConstantDetails"
                                    )}
                                  </Typography>
                                </>
                              )}
                              {selectedWarningType === "allTargetsConstant" && (
                                <>
                                  <Typography>
                                    {t("qualityCheck.allTargetsConstantText")}
                                  </Typography>
                                  <Typography>
                                    {t(
                                      "qualityCheck.allTargetsConstantDetails"
                                    )}
                                  </Typography>
                                </>
                              )}
                              {selectedWarningType ===
                                "colsWithIntolerableManyCategories" && (
                                <>
                                  <Typography>
                                    {selectedColumn}{" "}
                                    {t(
                                      "qualityCheck.colsWithIntolerableManyCategoriesText"
                                    )}
                                  </Typography>
                                  <Typography>
                                    {t(
                                      "qualityCheck.colsWithToManyCategoriesNumber"
                                    )}{" "}
                                    {numberCategies}
                                  </Typography>
                                  <Typography>
                                    {t(
                                      "qualityCheck.colsWithIntolerableManyCategoriesDetails"
                                    )}
                                  </Typography>
                                </>
                              )}
                            </>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ) : currentExperimentProgress.processed ? (
            <Grid item xs={3} sx={{ margin: theme.spacing(6) }}>
              <CircularProgress />
            </Grid>
          ) : (
            // if user lands on this page, without having all required steps fulfilled, show hint
            <Typography variant="body">{t("hints.NotYetProcessed")}</Typography>
          )
        }
      </Grid>
    </Grid>
  );
}
